import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { flushSync } from "react-dom";
import { useTheme } from "../../../shared/common/ThemeContext";
import { formatLocalDate, parseUtc } from "../../../shared/common/datetimeUtils";
import {
  apiMicrocycleContext,
  apiListSessionTemplates,
  apiGetSessionTemplate,
  apiSaveSessionTemplate,
  apiDeleteSessionTemplate,
  apiListMicrocycles,
  apiGetMicrocycle,
  apiSaveMicrocycle,
  apiDeleteMicrocycle,
  apiDeactivateScenario,
  apiDeactivateRun,
  apiLaunchRun,
  apiListRuns,
  apiListProtocols,
  apiGetCalendarMicrocycle,
  apiGetPlayerReferences,
  apiCleanupExpiredScenarios,
  apiGetRun,
  apiListMicrocyclesByMatch,
  apiGetMicrocycleByMatch,
} from "./predictiveApi";

import ScenarioTabs from "../components/prediction/ScenarioTabs";
import ScenarioToolbar from "../components/prediction/ScenarioToolbar";
import MicrocycleCalendar from "../components/prediction/MicrocycleCalendar";
import MicrocycleSelector from "../components/prediction/MicrocycleSelector";
import SessionLibrary from "../components/prediction/SessionLibrary";
import SessionEditorModal from "../components/prediction/SessionEditorModal";
import useRunPolling from "../components/prediction/useRunPolling";
import { uid } from "../components/prediction/uid";
import { useSeason } from "../../../shared/common/SeasonContext";
import { useScenarios } from "../../../shared/common/ScenarioContext";
import { STORAGE_PREFIX } from "@config";

const MAQUETTE_CLUB_ID = "7c61d7a5-7da7-409a-9bfd-18416640f640";
const MAQUETTE_SCENARIO_NAMES = ["scenario_A", "scenario_B", "scenario_C"];
const isMaquetteClub = () => {
  const cid = localStorage.getItem(`${STORAGE_PREFIX}club_id`);
  return cid === MAQUETTE_CLUB_ID;
};

const MAX_SCENARIOS = 20;
const MAX_SIMULATED = 3;

function normalizeMicrocycleFromApi(microcycleJson) {
  const sessions = (microcycleJson.sessions || []).map((s) => ({
    uid: uid("ms_"),
    id: s.id || null,
    microcycle_template_id: s.microcycle_template_id || microcycleJson.id,
    session_template_id: s.session_template_id || null,
    day_index: s.day_index ?? 0,
    start_min: s.start_min ?? 600,
    duration_min: s.duration_min ?? 90,
    label: s.label ?? (s.session_template?.name || "Session"),
    intensity_hint: s.intensity_hint ?? null,
    sort_order: s.sort_order ?? 0,
    session_template: s.session_template || null,
    event_type: s.event_type || "training",
    match_opponent: s.match_opponent || null,
  }));

  return {
    microcycle_template_id: microcycleJson.id || null,
    name: (microcycleJson.name || "scenario_new").startsWith("scenario_") ? (microcycleJson.name || "scenario_new") : `scenario_${microcycleJson.name || "new"}`,
    description: microcycleJson.description || "",
    days_count: microcycleJson.days_count ?? 7,
    variant: microcycleJson.variant || null,
    calendar_context: microcycleJson.calendar_context || {},
    locked: microcycleJson.locked || false,
    locked_by_run_id: microcycleJson.locked_by_run_id || null,
    active_until: microcycleJson.active_until || null,
    active_run_id: microcycleJson.active_run_id || null,
    is_active: microcycleJson.is_active || false,
    is_past: microcycleJson.is_past || false,
    sessions,
  };
}

function buildEmptyScenario({ name = "scenario_new", days_count = 7, calendar_context = {} } = {}) {
  return {
    microcycle_template_id: null,
    name,
    description: "",
    days_count,
    variant: null,
    calendar_context,
    sessions: [],
  };
}

function toSessions(microcycleDays, includeCalendarEvents = false) {
  const PERIOD_CFG = {
    morning:   { start_min: 10 * 60, duration_min: 90 },
    afternoon: { start_min: 16 * 60, duration_min: 90 },
  };

  const sessions = [];
  const playerSessionsMap = new Map();
  
  (microcycleDays || []).forEach((day, dayIndex) => {
    ["morning", "afternoon"].forEach((period) => {
      const list = day?.slots?.[period] || [];
      list.forEach((e) => {
        const cfg = PERIOD_CFG[period];
        const isMatch = e?.type === "match";
        const isTraining = e?.type === "training";
        const isPlayerSpecific = e?.is_player_specific === true || !!e?.player_id;

        if (!isMatch && !includeCalendarEvents) {
          return;
        }

        const eventStartMin = e?.start_min ?? cfg.start_min;
        const eventDurationMin = e?.duration_min ?? (isMatch ? 120 : cfg.duration_min);
        
        const label = isMatch
          ? `Match vs ${e?.opponent?.name ?? "?"}`
          : isTraining
            ? (e?.label || "Training")
            : "Rest";

        if (isPlayerSpecific) {
          const mergeKey = `${dayIndex}_${period}_${eventStartMin}_${eventDurationMin}_${label}_${e?.type || "training"}`;
          
          if (playerSessionsMap.has(mergeKey)) {
            const existingSession = playerSessionsMap.get(mergeKey);
            if (e?.player_id && !existingSession.player_ids.includes(e.player_id)) {
              existingSession.player_ids.push(e.player_id);
              if (e?.player_name) {
                existingSession.player_names.push(e.player_name);
              }
            }
            return;
          }
          
          const playerSession = {
            uid: crypto.randomUUID(),
            id: null,
            microcycle_template_id: null,
            session_template_id: e?.session_template_id || null,
            day_index: dayIndex,
            start_min: eventStartMin,
            duration_min: eventDurationMin,
            label,
            description: isTraining ? (e?.description || "") : "",
            intensity_hint: null,
            sort_order: sessions.length,
            session_template: e?.session_template_id 
              ? { id: e.session_template_id, name: e?.label || "Session" } 
              : null,
            calendar_event_id: e?.id || null,
            calendar_date: day?.date || null,
            calendar_period: period,
            event_type: e?.type || "training",
            match_opponent: isMatch ? (e?.opponent || null) : null,
            player_id: e?.player_id || null,
            player_ids: e?.player_id ? [e.player_id] : [],
            player_name: e?.player_name || null,
            player_names: e?.player_name ? [e.player_name] : [],
            is_player_specific: true,
          };
          
          playerSessionsMap.set(mergeKey, playerSession);
          sessions.push(playerSession);
          return;
        }

        sessions.push({
          uid: crypto.randomUUID(),
          id: null,
          microcycle_template_id: null,
          session_template_id: e?.session_template_id || null,
          day_index: dayIndex,
          start_min: eventStartMin,
          duration_min: eventDurationMin,
          label,
          description: isTraining ? (e?.description || "") : "",
          intensity_hint: null,
          sort_order: sessions.length,
          session_template: e?.session_template_id 
            ? { id: e.session_template_id, name: e?.label || "Session" } 
            : null,
          calendar_event_id: e?.id || null,
          calendar_date: day?.date || null,
          calendar_period: period,
          event_type: e?.type || "training", // "match", "training", "rest"
          match_opponent: isMatch ? (e?.opponent || null) : null,
          player_id: null,
          player_ids: [],
          player_name: null,
          player_names: [],
          is_player_specific: false,
        });
      });
    });
  });

  return sessions;
}


export default function PlanningMicrocycleLab() {
  const { isDarkMode } = useTheme();
  const shellCard = isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-600";

  const {
    scenarios,
    setScenarios,
    activeScenarioKey,
    setActiveScenarioKey,
    unsavedChanges,
    setUnsavedChanges,
    microcycleTemplates,
    setMicrocycleTemplates,
    autoLoadedFromDb,
    setAutoLoadedFromDb,
    activeMicrocycleMatchDate,
    setActiveMicrocycleMatchDate,
  } = useScenarios();

  // data
  const [context, setContext] = useState(null);
  const [sessionTemplates, setSessionTemplates] = useState([]);
  const [protocolTemplates, setProtocolTemplates] = useState([]);
  const [clubMicrocycle, setClubMicrocycle] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const { season } = useSeason();

  const [playerReferences, setPlayerReferences] = useState(null);
  const [refsLoading, setRefsLoading] = useState(true);
  
  const [calendarEventsAvailable, setCalendarEventsAvailable] = useState([]);

  // Microcycle selector state
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [selectorLoading, setSelectorLoading] = useState(false);
  const [matchMicrocycles, setMatchMicrocycles] = useState([]);
  const [microcycleLoading, setMicrocycleLoading] = useState(false);

  // No longer create an empty scenario on load — wait for microcycle selection
  // But ensure running simulations are always loaded
  useEffect(() => {
    if (autoLoadedFromDb) return;
    if (scenarios.length > 0) return;
    // Scenarios start empty — the selector will populate them
  }, [scenarios.length]);

  // Set active key if none is set
  useEffect(() => {
    if (!activeScenarioKey && scenarios.length > 0) {
      setActiveScenarioKey(scenarios[0].key);
    }
  }, [activeScenarioKey, scenarios, setActiveScenarioKey]);
  
  const lastSavedSessionsRef = useRef({});
  
  const scenariosRef = useRef(scenarios);
  useEffect(() => {
    scenariosRef.current = scenarios;
  }, [scenarios]);

  // Poll active/running runs for progress updates (every 5s)
  useRunPolling(scenarios, setScenarios, 30000);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingSessionTemplateId, setEditingSessionTemplateId] = useState(null);
  const [editingSessionTemplate, setEditingSessionTemplate] = useState(null);
  const [pendingCalendarPosition, setPendingCalendarPosition] = useState(null);

  const [launchingKey, setLaunchingKey] = useState(null);

  // Copy/paste calendar state: stores sessions keyed by their offset from match day
  const [copiedCalendar, setCopiedCalendar] = useState(null);

  const activeIdx = useMemo(
    () => scenarios.findIndex((s) => s.key === activeScenarioKey),
    [scenarios, activeScenarioKey]
  );
  const activeSlot = scenarios[activeIdx] !== undefined ? scenarios[activeIdx] : (scenarios.length > 0 ? scenarios[0] : null);

  const isActiveRunning = activeSlot?.status === "queued" || activeSlot?.status === "running";
  const isActiveScenarioPast = activeSlot?.scenario?.is_past || false;
  const isActiveScenarioActive = isActiveScenarioPast ? false : (activeSlot?.scenario?.is_active || false);
  const isActiveLocked = isActiveRunning || activeSlot?.scenario?.locked || isActiveScenarioActive || isActiveScenarioPast;
  const isActiveLaunching = launchingKey === activeSlot?.key;

  // Detect whether the active scenario has a target match session
  const hasTargetMatch = useMemo(() => {
    const sessions = activeSlot?.scenario?.sessions || [];
    return sessions.some(
      (s) => s.event_type === "match" || /\bmatch\b/i.test(String(s.label || ""))
    );
  }, [activeSlot?.scenario?.sessions]);

  const mcCtx = activeSlot?.scenario?.calendar_context || clubMicrocycle;


  const computedDayLabels = useMemo(() => {
    const fromApi = mcCtx?.day_labels;
    if (Array.isArray(fromApi) && fromApi.length) return fromApi;

    return ["MD-6", "MD-5", "MD-4", "MD-3", "MD-2", "MD-1", "MD-0"];
  }, [mcCtx]);

  const daysCount = (mcCtx?.days?.length) || activeSlot?.scenario?.days_count || computedDayLabels.length;

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        saveActiveScenario();
      }
    };
    
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeScenarioKey]);

  useEffect(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    setFromDate(`${yyyy}-${mm}-${dd}`);
  }, []);

  useEffect(() => {
    apiMicrocycleContext().then(setContext).catch(() => setContext(null));
    apiListSessionTemplates().then(setSessionTemplates).catch(() => setSessionTemplates([]));
    
    apiCleanupExpiredScenarios()
      .then(() => apiListMicrocycles())
      .then(setMicrocycleTemplates)
      .catch(() => setMicrocycleTemplates([]));
    
    apiListProtocols()
      .then(setProtocolTemplates)
      .catch(() => setProtocolTemplates([]));
    setRefsLoading(true);
    apiGetPlayerReferences()
      .then((data) => setPlayerReferences(data || null))
      .catch(() => setPlayerReferences(null))
      .finally(() => setRefsLoading(false));
  }, []);

  useEffect(() => {
    if (!season) return;

    // Load microcycle list for the selector
    setSelectorLoading(true);
    apiListMicrocyclesByMatch({ season })
      .then((data) => setMatchMicrocycles(data?.microcycles || []))
      .catch(() => setMatchMicrocycles([]))
      .finally(() => setSelectorLoading(false));

    // If a microcycle is already selected, load its calendar context
    if (activeMicrocycleMatchDate) {
      (async () => {
        try {
          const mc = await apiGetMicrocycleByMatch({ season, match_date: activeMicrocycleMatchDate });
          setClubMicrocycle(mc);
          setCalendarEventsAvailable(mc.calendar_events_available || []);

          setScenarios((prev) =>
            prev.map((s) => {
              if (s.key !== activeScenarioKey) return s;
              return {
                ...s,
                scenario: {
                  ...s.scenario,
                  days_count: mc.days?.length || s.scenario.days_count,
                  calendar_context: mc,
                },
              };
            })
          );
        } catch (e) {
          setClubMicrocycle(null);
          setCalendarEventsAvailable([]);
        }
      })();
    }
  }, [season, fromDate, activeMicrocycleMatchDate]);

  // When clubMicrocycle becomes available, inject match sessions into scenarios
  // that are missing them for this specific microcycle
  useEffect(() => {
    if (!clubMicrocycle?.days?.length) return;
    if (!activeMicrocycleMatchDate) return;

    const calendarMatchSessions = toSessions(clubMicrocycle.days, false);

    setScenarios((prev) => {
      let changed = false;
      const next = prev.map((s) => {
        const sessions = s.scenario?.sessions || [];
        const hasMatch = sessions.some(
          (sess) => sess.event_type === "match" || /\bmatch\b/i.test(String(sess.label || ""))
        );
        const hasCalendarCtx = s.scenario?.calendar_context?.days?.length > 0;

        if (hasMatch && hasCalendarCtx) return s;

        changed = true;

        if (hasMatch) {
          return {
            ...s,
            scenario: {
              ...s.scenario,
              calendar_context: clubMicrocycle,
              days_count: clubMicrocycle.days.length,
            },
          };
        }

        const newMatchSessions = calendarMatchSessions.map((m) => ({
          ...m,
          uid: uid("ms_"),
        }));
        return {
          ...s,
          scenario: {
            ...s.scenario,
            calendar_context: clubMicrocycle,
            days_count: clubMicrocycle.days.length,
            sessions: [...newMatchSessions, ...sessions],
          },
        };
      });
      return changed ? next : prev;
    });
  }, [clubMicrocycle, activeMicrocycleMatchDate]);

  const dayMeta = useMemo(() => {
    const days = mcCtx?.days || [];
    if (days.length) {
      return days.map((d) => ({
        md: (d.md || "").replace("MD-0", "MD"),
        date: d.date,
      }));
    }
    return computedDayLabels.map((x) => {
      const [md, date] = String(x).split(" · ").map((s) => s.trim());
      return { md: md || x, date: date || "" };
    });
  }, [mcCtx, computedDayLabels]);

  useEffect(() => {
    scenarios.forEach((slot) => {
      const currentSessionsJson = JSON.stringify(slot.scenario.sessions || []);
      const lastSavedJson = lastSavedSessionsRef.current[slot.key];
      
      if (lastSavedJson !== undefined) {
        const hasChanges = currentSessionsJson !== lastSavedJson;
        setUnsavedChanges((prev) => {
          if (prev[slot.key] !== hasChanges) {
            return { ...prev, [slot.key]: hasChanges };
          }
          return prev;
        });
      }
    });
  }, [scenarios]);

  const hasAnyUnsavedChanges = useMemo(() => {
    return Object.values(unsavedChanges).some(Boolean);
  }, [unsavedChanges]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasAnyUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "You have unsaved changes. Are you sure you want to leave?";
        return e.returnValue;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasAnyUnsavedChanges]);

  const handleSelectScenarioTab = useCallback((key) => {
    if (key === activeScenarioKey) return;
    
    if (unsavedChanges[activeScenarioKey]) {
      const confirmed = window.confirm(
        "The current scenario has unsaved changes. Are you sure you want to switch tabs?\n\nUnsaved changes will be lost."
      );
      if (!confirmed) return;
    }
    
    setActiveScenarioKey(key);
  }, [activeScenarioKey, unsavedChanges]);

  const updateScenario = (key, patchScenario) => {
    setScenarios((prev) =>
      prev.map((s) => (s.key === key ? { ...s, scenario: { ...s.scenario, ...patchScenario } } : s))
    );
  };

  const updateScenarioSessions = (key, nextSessions) => {
    setScenarios((prev) =>
      prev.map((s) => (s.key === key ? { ...s, scenario: { ...s.scenario, sessions: nextSessions } } : s))
    );
  };

  const addScenarioTab = () => {
    if (!activeMicrocycleMatchDate || !clubMicrocycle) {
      setSelectorOpen(true);
      return;
    }

    setScenarios((prev) => {
      if (prev.length >= MAX_SCENARIOS) return prev;

      const mc = clubMicrocycle;
      let newScenario;
      if (mc && mc.days) {
        const matchSessions = toSessions(mc.days, false);
        newScenario = {
          ...buildEmptyScenario({
            name: `scenario_${prev.length + 1}`,
            days_count: mc.days.length || context?.days_count || 7,
            calendar_context: mc,
          }),
          sessions: matchSessions,
        };
      } else {
        newScenario = buildEmptyScenario({
          name: `scenario_${prev.length + 1}`,
          days_count: context?.days_count || 7,
        });
      }

      const next = [
        ...prev,
        {
          key: uid("sc_"),
          status: "idle",
          lastRunId: null,
          scenario: newScenario,
        },
      ];
      return next;
    });
    setTimeout(() => {
      setScenarios((prev) => {
        const last = prev[prev.length - 1];
        if (last) setActiveScenarioKey(last.key);
        return prev;
      });
    }, 0);
  };

  const closeScenarioTab = async (key, deleteFromDb = false) => {
    const toClose = scenarios.find(s => s.key === key);
    
    if (deleteFromDb && toClose?.scenario?.microcycle_template_id) {
      try {
        await apiDeleteMicrocycle(toClose.scenario.microcycle_template_id);
        apiListMicrocycles().then(setMicrocycleTemplates).catch(() => {});
      } catch (error) {
        
      }
    }
    
    setScenarios((prev) => {
      const next = prev.filter((s) => s.key !== key);
      if (!next.length) {
        const fallback = {
          key: uid("sc_"),
          status: "idle",
          lastRunId: null,
          scenario: buildEmptyScenario({ name: "scenario_1", days_count: context?.days_count || 7 }),
        };
        setActiveScenarioKey(fallback.key);
        return [fallback];
      }
      if (activeScenarioKey === key) setActiveScenarioKey(next[0].key);
      return next;
    });
  };

  const duplicateScenario = (key) => {
    setScenarios((prev) => {
      if (prev.length >= MAX_SCENARIOS) return prev;
      const src = prev.find((s) => s.key === key);
      if (!src) return prev;
      const clone = JSON.parse(JSON.stringify(src.scenario));
      clone.name = clone.name.startsWith("scenario_") ? `${clone.name}_copie` : `scenario_${clone.name || "copie"}`;
      clone.microcycle_template_id = null;
      clone.sessions = (clone.sessions || []).map((x) => ({ ...x, uid: uid("ms_"), id: null }));
      const next = [...prev, { key: uid("sc_"), status: "idle", lastRunId: null, scenario: clone }];
      return next;
    });
    setTimeout(() => {
      setScenarios((prev) => {
        const last = prev[prev.length - 1];
        if (last) setActiveScenarioKey(last.key);
        return prev;
      });
    }, 0);
  };

  // ─── Copy / Paste calendar (relative to match day) ────────────────────
  const findMatchDayIndex = (sessions) => {
    const match = (sessions || []).find(
      (s) => s.event_type === "match" || /\bmatch\b/i.test(String(s.label || ""))
    );
    return match ? (match.day_index ?? -1) : -1;
  };

  const copyActiveCalendar = useCallback(() => {
    const sessions = activeSlot?.scenario?.sessions || [];
    if (!sessions.length) return;

    const matchIdx = findMatchDayIndex(sessions);
    if (matchIdx < 0) return; // nothing to anchor on

    // Store sessions with their offset relative to match day
    const copied = sessions
      .filter((s) => s.event_type !== "match") // exclude the match itself, target has its own
      .map((s) => ({
        ...s,
        _mdOffset: (s.day_index ?? 0) - matchIdx, // negative = before match, positive = after
      }));

    setCopiedCalendar({
      sessions: copied,
      sourceName: activeSlot?.scenario?.name || "unknown",
      sourceDaysCount: activeSlot?.scenario?.days_count || daysCount,
    });
  }, [activeSlot, daysCount]);

  const pasteCalendarToActive = useCallback(() => {
    if (!copiedCalendar || !activeSlot || isActiveLocked) return;

    const currentSessions = activeSlot.scenario?.sessions || [];
    const targetMatchIdx = findMatchDayIndex(currentSessions);
    if (targetMatchIdx < 0) return; // target has no match to anchor to

    const targetDaysCount = activeSlot.scenario?.days_count || daysCount;

    // Keep existing match sessions in target, replace everything else
    const matchSessions = currentSessions.filter(
      (s) => s.event_type === "match" || /\bmatch\b/i.test(String(s.label || ""))
    );

    // Remap copied sessions to target day indices relative to its match day
    const remapped = copiedCalendar.sessions
      .map((s) => {
        const newDayIndex = targetMatchIdx + s._mdOffset;
        if (newDayIndex < 0 || newDayIndex >= targetDaysCount) return null; // out of range
        return {
          ...s,
          uid: uid("ms_"),
          id: null,
          day_index: newDayIndex,
          microcycle_template_id: activeSlot.scenario?.microcycle_template_id || null,
          _mdOffset: undefined, // clean up
        };
      })
      .filter(Boolean);

    const nextSessions = [...matchSessions, ...remapped];
    updateScenarioSessions(activeSlot.key, nextSessions);
    setUnsavedChanges((prev) => ({ ...prev, [activeSlot.key]: true }));
  }, [copiedCalendar, activeSlot, isActiveLocked, daysCount]);

  const loadMicrocycleIntoNewTab = useCallback(async (microcycleId, run = null, calendarOverride = null) => {
    const mc = await apiGetMicrocycle(microcycleId);
    const normalized = normalizeMicrocycleFromApi(mc);

    const currentMc = calendarOverride || clubMicrocycle || (normalized.calendar_context?.days?.length ? normalized.calendar_context : null);
    const currentDays = currentMc?.days || [];

    const currentMdToIndex = new Map();
    currentDays.forEach((d, i) => {
      const md = (d.md || "").replace("MD-0", "MD").trim();
      if (md) currentMdToIndex.set(md, i);
    });

    const savedCtx = normalized.calendar_context || {};
    const savedDays = savedCtx.days || [];
    const savedIndexToMd = new Map();
    savedDays.forEach((d, i) => {
      const md = (d.md || "").replace("MD-0", "MD").trim();
      if (md) savedIndexToMd.set(i, md);
    });

    const hasMappingData = savedIndexToMd.size > 0 && currentMdToIndex.size > 0;

    // Helper: is session a match?
    const isMatchSession = (s) =>
      s.event_type === "match" || /\bmatch\b/i.test(String(s.label || ""));

    let remappedSessions;

    if (hasMappingData) {
      const trainingSessions = (normalized.sessions || []).filter((s) => !isMatchSession(s));

      const mapped = [];
      const unmappedLabels = new Set();
      for (const s of trainingSessions) {
        const savedMd = savedIndexToMd.get(s.day_index);
        if (!savedMd) {
          unmappedLabels.add(`day_index=${s.day_index}`);
          continue;
        }
        const newDayIndex = currentMdToIndex.get(savedMd);
        if (newDayIndex === undefined) {
          unmappedLabels.add(savedMd);
          continue;
        }
        mapped.push({
          ...s,
          uid: uid("ms_"),
          id: null,
          microcycle_template_id: null,
          day_index: newDayIndex,
        });
      }

      if (unmappedLabels.size > 0) {
   
      }

      const currentMatchSessions = toSessions(currentDays, false);

      remappedSessions = [...currentMatchSessions, ...mapped];
    } else {
      const currentDaysCount = currentDays.length || normalized.days_count || 7;
      const trainingSessions = (normalized.sessions || [])
        .filter((s) => !isMatchSession(s))
        .map((s) => ({
          ...s,
          uid: uid("ms_"),
          id: null,
          microcycle_template_id: null,
          day_index: Math.min(s.day_index, currentDaysCount - 1),
        }));

      const currentMatchSessions = currentDays.length > 0
        ? toSessions(currentDays, false)
        : [];

      remappedSessions = [...currentMatchSessions, ...trainingSessions];
    }

    const remappedScenario = {
      ...normalized,
      calendar_context: currentMc || normalized.calendar_context,
      days_count: currentDays.length || normalized.days_count,
      sessions: remappedSessions,
      // Active state from PredictiveRun (source of truth) if provided, else from microcycle API response
      is_active: run ? (run.is_active ?? false) : (mc.is_active ?? false),
      is_past: run ? (run.is_past ?? false) : (mc.is_past ?? false),
      locked: run ? (run.is_active || run.is_past || false) : (mc.locked ?? false),
      active_until: run ? (run.active_until ?? null) : (mc.active_until ?? null),
      active_run_id: run ? (run.id ?? null) : (mc.active_run_id ?? null),
      locked_by_run_id: run ? (run.id ?? null) : (mc.locked_by_run_id ?? null),
    };

    // Derive slot status from run data
    const slotStatus = run
      ? (run.is_active ? "active" : (run.status === "queued" || run.status === "running" ? run.status : "idle"))
      : "idle";
    const slotRunId = run ? (run.id ?? null) : null;
    // Progress tracking: if the run is done, mark computing as finished
    const slotRunProgress = run ? (typeof run.progress === "number" ? run.progress : 0) : 0;
    const slotComputingDone = run ? (run.status === "done") : false;

    setScenarios((prev) => {
      const canAdd = prev.length < MAX_SCENARIOS;

      if (canAdd) {
        const nextSlot = {
          key: uid("sc_"),
          status: slotStatus,
          lastRunId: slotRunId,
          runProgress: slotRunProgress,
          computingDone: slotComputingDone,
          scenario: remappedScenario,
        };

        lastSavedSessionsRef.current[nextSlot.key] = JSON.stringify(remappedScenario.sessions || []);

        const next = [...prev, nextSlot];

        setActiveScenarioKey(nextSlot.key);

        return next;
      }

      lastSavedSessionsRef.current[activeScenarioKey] = JSON.stringify(remappedScenario.sessions || []);
      return prev.map((s) =>
        s.key === activeScenarioKey
          ? { ...s, status: slotStatus, lastRunId: slotRunId, runProgress: slotRunProgress, computingDone: slotComputingDone, scenario: remappedScenario }
          : s
      );
    });
  }, [activeScenarioKey, clubMicrocycle]);


  const saveActiveScenario = async () => {
    const currentScenarios = scenariosRef.current;
    const currentActiveIdx = currentScenarios.findIndex((s) => s.key === activeScenarioKey);
    const currentSlot = currentScenarios[currentActiveIdx] || currentScenarios[0];
    
    if (!currentSlot?.scenario) {
     
      return;
    }

    const payload = {
      id: currentSlot.scenario.microcycle_template_id || undefined,
      name: currentSlot.scenario.name,
      description: currentSlot.scenario.description,
      days_count: currentSlot.scenario.days_count,
      variant: currentSlot.scenario.variant,
      calendar_context: currentSlot.scenario.calendar_context || {},
      sessions: (currentSlot.scenario.sessions || [])
        .map((s, i) => ({
          id: s.id || undefined,
          session_template_id: s.session_template_id || null,
          day_index: s.day_index,
          start_min: Math.round(s.start_min),
          duration_min: Math.round(s.duration_min),
          label: s.label || null,
          intensity_hint: s.intensity_hint ?? null,
          sort_order: s.sort_order ?? i,
          event_type: s.event_type || "training",
          match_opponent: s.match_opponent || null,
        })),
    };

    try {
      const saved = await apiSaveMicrocycle(payload);
      const normalized = normalizeMicrocycleFromApi(saved);
      
      setScenarios((prev) =>
        prev.map((s) =>
          s.key === currentSlot.key
            ? {
                ...s,
                status: "saved",
                scenario: normalized,
              }
            : s
        )
      );
      
      lastSavedSessionsRef.current[currentSlot.key] = JSON.stringify(normalized.sessions || []);
      setUnsavedChanges((prev) => ({ ...prev, [currentSlot.key]: false }));
      
      apiListMicrocycles().then(setMicrocycleTemplates).catch(() => {});
      
    } catch (error) {
     
      
      const errorText = error?.message || "";
      if (errorText.includes("409") || errorText.includes("duplicate_name")) {
        alert(`Cannot save: A scenario with name "${currentSlot.scenario.name}" already exists. Please choose a different name.`);
      }
      
      setScenarios((prev) =>
        prev.map((s) =>
          s.key === currentSlot.key ? { ...s, status: "error" } : s
        )
      );
    }
  };

  const launchActiveScenario = async () => {
    const slot = activeSlot;
    if (!slot?.scenario) return;
    if (slot.status === "queued" || slot.status === "running" || slot.status === "active") return;
    if (launchingKey) return;

    if (slot.scenario.is_past) {
      const activeUntil = slot.scenario.active_until;
      const dateLabel = activeUntil
        ? formatLocalDate(activeUntil, { locale: 'en-US', day: 'numeric', month: 'long', year: 'numeric' })
        : "unknown";
      alert(`Cannot launch this scenario: the target date (${dateLabel}) is in the past.`);
      return;
    }

    if (slot.scenario.active_until) {
      // parseUtc gère les dates "YYYY-MM-DD" sans décalage de jour lié à la timezone
      const targetDate = parseUtc(slot.scenario.active_until);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      targetDate.setHours(0, 0, 0, 0);
      if (targetDate < today) {
        const dateLabel = formatLocalDate(slot.scenario.active_until, { locale: 'en-US', day: 'numeric', month: 'long', year: 'numeric' });
        alert(`Cannot launch this scenario: the target date (${dateLabel}) is in the past.`);
        return;
      }
    }

    const currentSimulatedCount = scenarios.filter((s) => {
      const isSimulating = s.status === "active" || s.status === "queued" || s.status === "running" || s.scenario?.is_active;
      return isSimulating && s.key !== slot.key;
    }).length;
    if (currentSimulatedCount >= MAX_SIMULATED) {
      alert(`You cannot launch more than ${MAX_SIMULATED} simulations simultaneously.`);
      return;
    }

    setLaunchingKey(slot.key);

    try {
      let microcycleId = slot.scenario.microcycle_template_id;
      if (!microcycleId) {
        await saveActiveScenario();
        const latestScenarios = scenariosRef.current;
        microcycleId = latestScenarios.find((s) => s.key === slot.key)?.scenario?.microcycle_template_id || null;
      }
      if (!microcycleId) {
        setLaunchingKey(null);
        return;
      }

      const response = await apiLaunchRun({
        name: slot.scenario.name,
        variant: slot.scenario.name,
        microcycle_template_id: microcycleId,
        parameters: {},
      });

      // Get active_until from the run response (source of truth)
      const runData = response.run || {};
      const updatedScenario = {
        ...slot.scenario,
        locked: true,
        is_active: true,
        is_past: false,
        active_until: runData.active_until || slot.scenario.active_until || null,
        active_run_id: runData.id || null,
        locked_by_run_id: runData.id || null,
      };

      setScenarios((prev) =>
        prev.map((s) =>
          s.key === slot.key
            ? {
                ...s,
                status: "active",
                lastRunId: runData.id || null,
                runProgress: 0,
                computingDone: false,
                scenario: updatedScenario,
              }
            : s
        )
      );
    } catch (error) {
      
      
      const errorText = error?.message || "";
      if (errorText.includes("scenario_target_date_in_past") || errorText.includes("date cible")) {
        alert("Cannot launch this scenario: the target date is in the past.");
      } else if (errorText.includes("max_active_reached") || errorText.includes("Maximum 3")) {
        alert(`Maximum ${MAX_SIMULATED} active simulations reached. Deactivate one before launching a new one.`);
      }
      
      setScenarios((prev) =>
        prev.map((s) =>
          s.key === slot.key ? { ...s, status: "error" } : s
        )
      );
    } finally {
      setLaunchingKey(null);
    }
  };

 const openSessionEditor = async (sessionTemplateId) => {
  setEditorOpen(true);

  if (!sessionTemplateId) {
    setEditingSessionTemplate({
      id: null,
      name: "",
      description: "",
      blocks: [],
      tags: [],
    });
    return;
  }

  try {
    const full = await apiGetSessionTemplate(sessionTemplateId);
    setEditingSessionTemplate(full);
  } catch {
    setEditingSessionTemplate(null);
  }
};


  const closeEditor = () => {
    setEditorOpen(false);
    setEditingSessionTemplateId(null);
    setEditingSessionTemplate(null);
    setPendingCalendarPosition(null);
  };

  const saveEditedSessionTemplate = async (payload) => {
    const saved = await apiSaveSessionTemplate(payload);
    setEditingSessionTemplate(saved);
    apiListSessionTemplates().then(setSessionTemplates).catch(() => {});

    const savedTotalDuration = (saved?.blocks || payload.blocks || []).reduce(
      (sum, b) => sum + (b.duration_min || 0) + (b.rest_duration_min || 0),
      0
    ) || 90;

    if (pendingCalendarPosition && saved?.id) {
      const { dayIndex, startMin, sessionUid } = pendingCalendarPosition;
      
      if (sessionUid) {
        setScenarios((prev) =>
          prev.map((s) => {
            if (s.key !== activeScenarioKey) return s;
            return {
              ...s,
              scenario: {
                ...s.scenario,
                sessions: (s.scenario.sessions || []).map((sess) =>
                  sess.uid === sessionUid
                    ? {
                        ...sess,
                        session_template_id: saved.id,
                        label: saved.name,
                        description: saved.description || "",
                        duration_min: savedTotalDuration,
                        session_template: { id: saved.id, name: saved.name },
                      }
                    : sess
                ),
              },
            };
          })
        );
      } else {
        onDropNewSessionFromLibrary(
          { id: saved.id, name: saved.name },
          dayIndex,
          startMin,
          savedTotalDuration
        );
      }
      
      setPendingCalendarPosition(null);
      setEditorOpen(false);
      setEditingSessionTemplate(null);
    } else if (saved?.id) {
      setScenarios((prev) =>
        prev.map((s) => ({
          ...s,
          scenario: {
            ...s.scenario,
            sessions: (s.scenario.sessions || []).map((sess) =>
              sess.session_template_id === saved.id
                ? {
                    ...sess,
                    label: saved.name,
                    description: saved.description || "",
                    duration_min: savedTotalDuration,
                    session_template: { id: saved.id, name: saved.name },
                  }
                : sess
            ),
          },
        }))
      );
    }

    return saved;
  };

  const handleDeleteSessionTemplate = async (sessionId) => {
    try {
      await apiDeleteSessionTemplate(sessionId);
      apiListSessionTemplates().then(setSessionTemplates).catch(() => {});
    } catch (error) {
      
    }
  };

  const handleCreateNewSessionFromCalendar = ({ dayIndex, startMin, durationMin }) => {
    setPendingCalendarPosition({ dayIndex, startMin, durationMin });
    setEditorOpen(true);
    setEditingSessionTemplate({
      id: null,
      name: "",
      description: "",
      blocks: [],
      tags: [],
    });
  };

  const handleEditSession = async (session) => {
    if (!session) return;
    
    setPendingCalendarPosition({
      dayIndex: session.day_index,
      startMin: session.start_min,
      durationMin: session.duration_min,
      sessionUid: session.uid,
    });
    
    setEditorOpen(true);
    
    if (session.session_template_id) {
      try {
        const full = await apiGetSessionTemplate(session.session_template_id);
        setEditingSessionTemplate(full);
      } catch {
        setEditingSessionTemplate({
          id: null,
          name: session.label || "Session",
          description: session.description || "",
          blocks: [],
          tags: [],
        });
      }
    } else {
      setEditingSessionTemplate({
        id: null,
        name: session.label || "Session",
        description: session.description || "",
        blocks: [],
        tags: [],
      });
    }
  };

  const onDropNewSessionFromLibrary = async (sessionTemplate, dayIndex, startMin, durationMin = 90) => {
    const slot = activeSlot;
    const scenario = slot.scenario;

    const existingSessions = scenario.sessions || [];
    const newEndMin = startMin + durationMin;
    
    const collision = existingSessions.find(s => {
      if (s.day_index !== dayIndex) return false;
      if (s.player_id || s.player_ids?.length > 0) return false;
      if (s.event_type === "match") return false;
      
      const sStart = s.start_min;
      const sEnd = sStart + s.duration_min;
      
      return !(sEnd <= startMin || newEndMin <= sStart);
    });
    
    if (collision) {
      alert(`Cannot place this session: it would overlap with "${collision.label}" (${Math.floor(collision.start_min/60)}h${String(collision.start_min%60).padStart(2,'0')} - ${collision.duration_min}min)`);
      return;
    }

    const period = startMin < 12 * 60 ? "morning" : "afternoon";
    const dayDate = dayMeta[dayIndex]?.date;
    const sessionUid = uid("ms_");

    const next = [
      ...(scenario.sessions || []),
      {
        uid: sessionUid,
        id: null,
        microcycle_template_id: scenario.microcycle_template_id || null,
        session_template_id: sessionTemplate.id,
        day_index: dayIndex,
        start_min: startMin,
        duration_min: durationMin,
        label: sessionTemplate.name,
        description: "",
        intensity_hint: null,
        sort_order: (scenario.sessions || []).length,
        session_template: { id: sessionTemplate.id, name: sessionTemplate.name },
        calendar_date: dayDate || null,
        calendar_period: period,
        event_type: "training",
      },
    ];

    updateScenario(activeSlot.key, { auto_generated: false });
    updateScenarioSessions(slot.key, next);
  };

  const onUpdateCalendarSessions = (nextSessions) => {
    updateScenario(activeSlot.key, { auto_generated: false });
    updateScenarioSessions(activeSlot.key, nextSessions);
  };

  const handleUpdateSessionDetails = useCallback(async (details) => {
  }, []);

  const handleRemoveSession = useCallback(async (session) => {
  }, []);

  const handleSessionMoved = useCallback(async (movedSession) => {
  }, []);

  const deleteActiveScenario = async () => {
    const slot = activeSlot;
    if (!slot?.scenario?.microcycle_template_id) return;
    
    try {
      await apiDeleteMicrocycle(slot.scenario.microcycle_template_id);
      closeScenarioTab(slot.key, false);
      apiListMicrocycles().then(setMicrocycleTemplates).catch(() => {});
    } catch (error) {
      
    }
  };

  const deactivateActiveScenario = async () => {
    const slot = activeSlot;
    if (!slot) return;

    try {
      const runId = slot.lastRunId || slot.scenario?.active_run_id;
      if (runId) {
        await apiDeactivateRun(runId);
      }
      if (slot.scenario?.microcycle_template_id) {
        await apiDeactivateScenario(slot.scenario.microcycle_template_id);
      }
      setScenarios((prev) =>
        prev.map((s) =>
          s.key === slot.key
            ? {
                ...s,
                status: "idle",
                lastRunId: null,
                runProgress: 0,
                computingDone: false,
                scenario: {
                  ...s.scenario,
                  is_active: false,
                  is_past: false,
                  active_until: null,
                  active_run_id: null,
                  locked: false,
                  locked_by_run_id: null,
                },
              }
            : s
        )
      );
    } catch (error) {
      
    }
  };

  // Auto-load active/running simulations on page load (always visible regardless of selected microcycle)
  useEffect(() => {
    if (autoLoadedFromDb) return;
    if (!Array.isArray(microcycleTemplates) || microcycleTemplates.length === 0) return;

    setAutoLoadedFromDb(true);

    (async () => {
      try {
        // --- Maquette club: force-load scenario_A, scenario_B, scenario_C ---
        if (isMaquetteClub()) {
          const maquetteTemplates = MAQUETTE_SCENARIO_NAMES
            .map((name) => {
              // Exact match first, then case-insensitive fallback
              return microcycleTemplates.find((t) => t.name === name)
                || microcycleTemplates.find((t) => t.name.toLowerCase() === name.toLowerCase());
            });

          let referenceCalendarCtx = null;
          for (const tpl of maquetteTemplates) {
            if (!tpl) continue;
            await loadMicrocycleIntoNewTab(tpl.id, null);
            // Keep the first loaded template's calendar_context as reference for fallbacks
            if (!referenceCalendarCtx) {
              try {
                const mc = await apiGetMicrocycle(tpl.id);
                referenceCalendarCtx = mc?.calendar_context || null;
              } catch { /* ignore */ }
            }
          }

          // Create fallback scenarios for any templates not found in DB
          const missingNames = MAQUETTE_SCENARIO_NAMES.filter((_, i) => !maquetteTemplates[i]);
          for (const name of missingNames) {
            const daysCount = referenceCalendarCtx?.days?.length || 7;
            const fallbackSessions = referenceCalendarCtx?.days?.length
              ? toSessions(referenceCalendarCtx.days, false)
              : [];
            const fallback = {
              ...buildEmptyScenario({ name, days_count: daysCount, calendar_context: referenceCalendarCtx || {} }),
              sessions: fallbackSessions.map((s) => ({ ...s, uid: uid("ms_") })),
            };
            setScenarios((prev) => [
              ...prev,
              { key: uid("sc_"), status: "idle", lastRunId: null, scenario: fallback },
            ]);
          }
          return;
        }

        // --- Normal flow: only auto-load active/running simulations ---
        let runs = [];
        try { runs = await apiListRuns(); } catch { /* ignore */ }

        const activeRuns = runs.filter((r) => r.is_active === true && r.microcycle_template_id);
        const pendingRuns = runs.filter(
          (r) => (r.status === "queued" || r.status === "running") && r.microcycle_template_id
        );

        const toLoad = [...activeRuns, ...pendingRuns];
        const seen = new Set();
        const deduped = [];
        for (const r of toLoad) {
          if (!seen.has(r.microcycle_template_id)) {
            seen.add(r.microcycle_template_id);
            deduped.push(r);
          }
        }

        if (deduped.length === 0) return;

        for (const run of deduped) {
          await loadMicrocycleIntoNewTab(run.microcycle_template_id, run);
        }
      } catch (e) {
        // ignore
      }
    })();
  }, [microcycleTemplates, loadMicrocycleIntoNewTab, autoLoadedFromDb, setAutoLoadedFromDb, setScenarios, setActiveScenarioKey]);

  const handleImportCalendarEvents = useCallback(async () => {
    let events = calendarEventsAvailable;

    // If no cached events, re-fetch from the calendar API
    if (!events.length) {
      try {
        const mc = await apiGetCalendarMicrocycle({ season, from: fromDate || undefined });
        events = mc.calendar_events_available || [];
        if (mc.days) {
          setClubMicrocycle(mc);
        }
        setCalendarEventsAvailable(events);
      } catch (e) {
        
      }
    }

    if (!events.length) {
      alert("No calendar events available for import.");
      return;
    }

    const PERIOD_CFG = {
      morning:   { start_min: 10 * 60, duration_min: 90 },
      afternoon: { start_min: 16 * 60, duration_min: 90 },
    };

    let importedCount = 0;
    const failedImports = [];

    flushSync(() => {
      setScenarios((prevScenarios) => {
        const currentActiveIdx = prevScenarios.findIndex((s) => s.key === activeScenarioKey);
        const slot = prevScenarios[currentActiveIdx] || prevScenarios[0];
        
        if (!slot?.scenario) return prevScenarios;
        
        const scenario = slot.scenario;
        const existingSessions = scenario.sessions || [];
        const importedSessions = [];

      events.forEach((event) => {
        const { day_index, period, date } = event;
        const cfg = PERIOD_CFG[period] || PERIOD_CFG.morning;
        
        const startMin = event.start_min ?? cfg.start_min;
        const durationMin = event.duration_min ?? cfg.duration_min;
        const newEndMin = startMin + durationMin;
        
        const label = event.label || event.type || "Event";
        
        const collision = existingSessions.concat(importedSessions).find(s => {
          if (s.day_index !== day_index) return false;
          if (s.player_id || s.player_ids?.length > 0) return false;
          if (s.event_type === "match") return false;
          
          const sStart = s.start_min;
          const sEnd = sStart + s.duration_min;
          
          return !(sEnd <= startMin || newEndMin <= sStart);
        });
        
        if (collision) {
          failedImports.push({
            event,
            label,
            date,
            period,
            reason: `Collision with "${collision.label}" (${Math.floor(collision.start_min/60)}h${String(collision.start_min%60).padStart(2,'0')} - ${collision.duration_min}min)`,
          });
          return;
        }
        
        const newSession = {
          uid: uid("ms_"),
          id: null,
          microcycle_template_id: scenario.microcycle_template_id || null,
          session_template_id: event.session_template_id || null,
          day_index: day_index,
          start_min: startMin,
          duration_min: durationMin,
          label: label,
          description: event.description || "",
          intensity_hint: null,
          sort_order: existingSessions.length + importedSessions.length,
          session_template: event.session_template_id 
            ? { id: event.session_template_id, name: label } 
            : null,
          calendar_event_id: event.id || null,
          calendar_date: date || null,
          calendar_period: period,
          event_type: event.type || "training",
          player_id: event.player_id || null,
          player_ids: event.player_id ? [event.player_id] : [],
          player_name: event.player_name || null,
          player_names: event.player_name ? [event.player_name] : [],
          is_player_specific: event.is_player_specific || false,
        };
        
        importedSessions.push(newSession);
      });

      importedCount = importedSessions.length;

      if (importedSessions.length === 0) {
        return prevScenarios;
      }

      const nextSessions = [...existingSessions, ...importedSessions];
      
      return prevScenarios.map((s) =>
        s.key === slot.key
          ? {
              ...s,
              scenario: {
                ...s.scenario,
                auto_generated: false,
                sessions: nextSessions,
              },
            }
          : s
      );
    });
    });

    // Show alert after state update
    if (failedImports.length > 0) {
      const failedMessages = failedImports.map(f => 
        `• "${f.label}" on ${f.date} (${f.period === 'morning' ? 'morning' : 'afternoon'})\n  → ${f.reason}`
      ).join('\n\n');
      
      alert(
        `Partial import: ${importedCount} event(s) imported, ${failedImports.length} not placed.\n\n` +
        `Events not placed:\n\n${failedMessages}`
      );
    } else if (importedCount > 0) {
      alert(`${importedCount} event(s) imported successfully.`);
    }
    
    // Clear available events after import attempt
    setCalendarEventsAvailable([]);
  }, [calendarEventsAvailable, activeScenarioKey, season, fromDate]);

  // --- Microcycle selector handlers ---
  const openMicrocycleSelector = useCallback(async () => {
    setSelectorOpen(true);
    if (!matchMicrocycles.length && season) {
      setSelectorLoading(true);
      try {
        const data = await apiListMicrocyclesByMatch({ season });
        setMatchMicrocycles(data?.microcycles || []);
      } catch { setMatchMicrocycles([]); }
      finally { setSelectorLoading(false); }
    }
  }, [matchMicrocycles.length, season]);

  const handleSelectMicrocycle = useCallback(async (mc) => {
    setSelectorOpen(false);
    setMicrocycleLoading(true);

    const matchDate = mc.match_date;
    setActiveMicrocycleMatchDate(matchDate);

    try {
      // Load the calendar context for this match-based microcycle
      const calendarData = await apiGetMicrocycleByMatch({ season, match_date: matchDate });
      setClubMicrocycle(calendarData);
      setCalendarEventsAvailable(calendarData.calendar_events_available || []);

      // Load saved scenarios for this microcycle
      const savedScenarios = mc.scenarios || [];

      // Clear current non-active scenarios and load those for this microcycle
      setScenarios((prev) => {
        // Keep only active/running simulations from other microcycles
        const activeFromOther = prev.filter((s) => {
          const isRunning = s.status === "active" || s.status === "queued" || s.status === "running";
          return isRunning;
        });
        return activeFromOther;
      });

      if (savedScenarios.length > 0) {
        // Load saved scenarios from DB
        for (const sc of savedScenarios) {
          const run = sc.is_active || sc.run_status === "queued" || sc.run_status === "running"
            ? { id: null, is_active: sc.is_active, is_past: sc.is_past, status: sc.run_status, progress: sc.run_progress, active_until: null }
            : null;

          // Check if this scenario is already loaded (from active runs)
          const alreadyLoaded = scenariosRef.current.some(
            (s) => s.scenario?.microcycle_template_id === sc.id
          );
          if (alreadyLoaded) continue;

          await loadMicrocycleIntoNewTab(sc.id, run, calendarData);
        }
      } else {
        // No saved scenarios — create a new empty one for this microcycle
        const matchSessions = toSessions(calendarData.days || [], false);
        const newScenario = {
          ...buildEmptyScenario({
            name: `scenario_1`,
            days_count: calendarData.days?.length || 7,
            calendar_context: calendarData,
          }),
          sessions: matchSessions,
        };
        const newKey = uid("sc_");
        setScenarios((prev) => [
          ...prev,
          { key: newKey, status: "idle", lastRunId: null, scenario: newScenario },
        ]);
        setActiveScenarioKey(newKey);
      }
    } catch (e) {
      // ignore
    } finally {
      setMicrocycleLoading(false);
    }
  }, [season, loadMicrocycleIntoNewTab, setActiveMicrocycleMatchDate, setScenarios, setActiveScenarioKey]);

  const hasMicrocycleSelected = !!activeMicrocycleMatchDate;

return (
  <div className="space-y-4">

    {/* Microcycle Selector Modal */}
    <MicrocycleSelector
      isDarkMode={isDarkMode}
      isOpen={selectorOpen}
      onClose={() => setSelectorOpen(false)}
      microcycles={matchMicrocycles}
      loading={selectorLoading}
      onSelectMicrocycle={handleSelectMicrocycle}
      activeMicrocycleMatchDate={activeMicrocycleMatchDate}
    />

    {/* No microcycle selected — show prompt */}
    {!hasMicrocycleSelected && scenarios.length === 0 && !microcycleLoading ? (
      <div className={`rounded-2xl border ${shellCard} p-12 flex flex-col items-center justify-center gap-6`}>
        <div className={`flex h-20 w-20 items-center justify-center rounded-2xl ${
          isDarkMode ? "bg-indigo-500/15" : "bg-indigo-100"
        }`}>
          <svg className={`h-10 w-10 ${isDarkMode ? "text-indigo-400" : "text-indigo-600"}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
          </svg>
        </div>
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Select a microcycle</h2>
          <p className={`text-sm max-w-md ${muted}`}>
            Choose a microcycle from the calendar to start planning your simulation scenarios.
            Each microcycle is linked to a target match.
          </p>
        </div>
        <button
          onClick={openMicrocycleSelector}
          className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-6 py-3 text-sm font-semibold text-white transition hover:bg-indigo-700"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
          </svg>
          Open microcycle calendar
        </button>
      </div>
    ) : microcycleLoading ? (
      <div className={`rounded-2xl border ${shellCard} p-12 flex flex-col items-center justify-center gap-4`}>
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500" />
        <p className={muted}>Loading microcycle…</p>
      </div>
    ) : (
      <>
        {/* Microcycle header bar */}
        {activeMicrocycleMatchDate && (
          <div className={`rounded-2xl border p-3 flex items-center justify-between ${shellCard}`}>
            <div className="flex items-center gap-3">
              <button
                onClick={openMicrocycleSelector}
                className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition ${
                  isDarkMode
                    ? "bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-300 border border-indigo-500/40"
                    : "bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200"
                }`}
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                </svg>
                Change microcycle
              </button>
              <div className={`text-sm ${muted}`}>
                <span className="font-medium">Target match:</span>{" "}
                {new Date(activeMicrocycleMatchDate + "T00:00:00").toLocaleDateString("en-US", {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
                {clubMicrocycle?.match_info?.opponent?.name && (
                  <span className="ml-2">
                    vs <span className="font-semibold">{clubMicrocycle.match_info.opponent.name}</span>
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Scenario tabs */}
        <ScenarioTabs
          isDarkMode={isDarkMode}
          scenarios={scenarios}
          activeKey={activeScenarioKey}
          onSelect={handleSelectScenarioTab}
          onAdd={addScenarioTab}
          onClose={(key) => closeScenarioTab(key, false)}
          onDelete={(key) => closeScenarioTab(key, true)}
          onDuplicate={duplicateScenario}
          maxScenarios={MAX_SCENARIOS}
          maxSimulated={MAX_SIMULATED}
          unsavedChanges={unsavedChanges}
        />

        {!activeSlot ? (
          <div className={`rounded-2xl border ${shellCard} p-8 text-center`}>
            <p className={muted}>No scenario. Add one with the + button above.</p>
          </div>
        ) : (
          <>
            {/* Scenario toolbar */}
            <ScenarioToolbar
              isDarkMode={isDarkMode}
              microcycleTemplates={microcycleTemplates}
              activeSlot={activeSlot}
              onRename={(name) => !isActiveLocked && activeSlot && updateScenario(activeSlot.key, { name })}
              onSaveScenario={saveActiveScenario}
              onLaunchScenario={launchActiveScenario}
              onDeactivateScenario={deactivateActiveScenario}
              onDeleteScenario={deleteActiveScenario}
              canAddScenario={scenarios.length < MAX_SCENARIOS}
              hasUnsavedChanges={unsavedChanges[activeScenarioKey]}
              isLaunching={isActiveLaunching}
              hasTargetMatch={hasTargetMatch}
              onSearchTargetMatch={isActiveLocked ? undefined : handleImportCalendarEvents}
              onCopyCalendar={copyActiveCalendar}
              onPasteCalendar={pasteCalendarToActive}
              hasCopiedCalendar={!!copiedCalendar}
            />

            {/* Main content: left blocks / right calendar */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">

              {/* Left: session / blocks library */}
              <div className="lg:col-span-4">
                <div className={`rounded-2xl border ${shellCard} p-4`}>
                  <h2 className="text-lg font-semibold">Session library</h2>
                  <p className={`${muted} text-sm mt-1`}>
                    Drag and drop a session into the calendar.
                    Double-click to edit the session.
                  </p>

                  <div className="mt-4">
                    <button
                      onClick={() => openSessionEditor(null)}
                      className="mb-3 inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700"
                    >
                      + New session
                    </button>

                    <SessionLibrary
                      isDarkMode={isDarkMode}
                      sessionTemplates={sessionTemplates}
                      onRefresh={() => apiListSessionTemplates().then(setSessionTemplates)}
                      onDoubleClickSession={(id) => openSessionEditor(id)}
                      onDeleteSession={handleDeleteSessionTemplate}
                    />
                  </div>
                </div>
              </div>

              {/* Right: microcycle calendar */}
              <div className="lg:col-span-8">
                <MicrocycleCalendar
                  isDarkMode={isDarkMode}
                  dayLabels={computedDayLabels}
                  dayMeta={dayMeta}
                  daysCount={daysCount}
                  sessions={activeSlot?.scenario?.sessions || []}
                  onChangeSessions={isActiveLocked ? () => {} : onUpdateCalendarSessions}
                  onDropFromLibrary={isActiveLocked ? () => {} : onDropNewSessionFromLibrary}
                  sessionTemplates={sessionTemplates}
                  onDoubleClickSession={(sessionTemplateId) =>
                    !isActiveLocked && openSessionEditor(sessionTemplateId)
                  }
                  onCreateNewSession={isActiveLocked ? () => {} : handleCreateNewSessionFromCalendar}
                  onUpdateSessionDetails={isActiveLocked ? () => {} : handleUpdateSessionDetails}
                  onEditSession={isActiveLocked ? () => {} : handleEditSession}
                  onRemoveSession={isActiveLocked ? () => {} : handleRemoveSession}
                  onSessionMoved={isActiveLocked ? () => {} : handleSessionMoved}
                  calendarEventsAvailable={isActiveLocked ? [] : calendarEventsAvailable}
                  onImportCalendarEvents={isActiveLocked ? () => {} : handleImportCalendarEvents}
                  readOnly={isActiveLocked}
                />
              </div>

            </div>

            <SessionEditorModal
              isDarkMode={isDarkMode}
              open={editorOpen}
              onClose={closeEditor}
              sessionTemplate={editingSessionTemplate}
              protocolTemplates={protocolTemplates}
              onSave={saveEditedSessionTemplate}
              playerReferences={playerReferences}
              refsLoading={refsLoading}
            />
          </>
        )}
      </>
    )}

  </div>
);

}
